/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Maximize2, Minimize2, Plus, RefreshCcw } from "react-feather";
import { FaRegBookmark } from "react-icons/fa";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getAllCharges } from "../../../api/finance/addChargesApi";
import {
  calculateCreditDebitTax,
  createCreditDebitDraft,
  createCreditDebitNote,
  deleteCreditDebitDraft,
  updateCreditDebitDraft,
} from "../../../api/finance/creditDebitNoteApi";
import {
  getBulkDropdownData,
  getFieldValuesByTableAndId,
} from "../../../api/keyvalue";
import { activeById, FORMS, MODULES, statusById } from "../../../const";
import useUserCompany from "../../../hooks/useUserCompany";
import Grid from "../../Grid";
import Button from "../../input/Button";
import Dropdown from "../../input/Dropdown";
import Input from "../../input/Input";
import LoadingSpinner from "../../LoadingSpinner";
import ButtonRenderer from "../../renderer/ButtonRenderer";
import SelectRenderer from "../../renderer/SelectRenderer";
import {
  CurrencyRenderer,
  DynamicCurrencyRenderer,
  ResizableScrollingTextRenderer,
} from "../../renderer/StyleRenderer";
import ChargeTaxBreakdown from "../EvInv/ChargeTaxBreakdown";

const noteCategory = {
  CREDIT: 1,
  DEBIT: 2,
};

const invoiceCategory = {
  SINGLE: 1,
  BULK: 2,
};

const AccordionContent = ({ isOpen, children }) => {
  const contentRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState("0px");

  useEffect(() => {
    if (contentRef.current) {
      // When open, set maxHeight to the scrollHeight; when closed, set to 0
      setMaxHeight(isOpen ? `${contentRef.current.scrollHeight}px` : "0px");
    }
  }, [isOpen, children]);

  return (
    <div
      style={{
        maxHeight: maxHeight,
        overflowY: "auto",
        opacity: isOpen ? 1 : 0,
        transition: "max-height 0.5s ease, opacity 0.5s ease",
        padding: "0 1rem 0.5rem 1rem",
      }}
    >
      <div ref={contentRef}>{children}</div>
    </div>
  );
};

const SummaryDetail = ({ data }) => {
  // Destructure the main fields from the passed data
  const {
    eihType,
    eihCode,
    eihWithoutTax,
    eihWithTax,
    chargeList,
    eihCurrency,
    eihActive,
    eihStatus,
    eihInvCustomer,
    eihBillingCustomer,
    eihTransporterName,
    eihInvCustomerName,
    createdDate,
  } = data;

  const userCompany = useUserCompany();
  const userCurrency = userCompany ? userCompany.comCurrency : null;
  const userCurrencyCode = userCompany ? userCompany.comCurrencyCode : null;
  const [statusName, setStatusName] = useState("");
  const [activeName, setActiveName] = useState("");
  const [currencyName, setCurrencyName] = useState(userCurrencyCode);
  // const [invoiceCustomerName, setInvoiceCustomerName] = useState("");
  const [billingCustomerName, setBillingCustomerName] = useState("");
  const [jobOptions, setJobOptions] = useState([]);
  const [jobId, setJobId] = useState("");
  const [jobObject, setJobObject] = useState({});

 const formatToTwoDecimalPlaces = (value) => {
    if (value == null || value === "N/A") {
      return "N/A";
    }

    const num = parseFloat(value);
    if (isNaN(num)) return "N/A";

    return Number(num.toFixed(2)); // convert back to number!
  };

  const formatValuesWithCurrency = (value) => {
    if (value == null || value === "N/A") {
      return "N/A";
    }
    const formatter = Intl.NumberFormat("en-US", {
      style: "currency",
      currency: userCurrencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return formatter.format(value);
  };

  useEffect(() => {
    (async () => {
      const activeName = activeById[eihActive];
      setActiveName(activeName);
      const statusName = statusById[eihStatus];
      setStatusName(statusName);

      const currencyDetails = await getFieldValuesByTableAndId({
        tableName: "masterdata.tms_vm_reg_currency",
        primaryKey: "cur_id",
        fieldNames: "cur_currency",
        ids: eihCurrency.toString(),
      });
      const currName =
        currencyDetails && currencyDetails.length > 0
          ? currencyDetails[0].field1
          : "";
      setCurrencyName(currName);

      // const invCustomer = await getFieldValuesByTableAndId({
      //   tableName: "masterdata.tms_m_stakeholder",
      //   primaryKey: "stk_id",
      //   fieldNames: "stk_name",
      //   ids: eihInvCustomer ? eihInvCustomer.toString() : "",
      // });

      // const invCustomerName =
      //   invCustomer && invCustomer.length > 0 ? invCustomer[0].field1 : "";
      // setInvoiceCustomerName(invCustomerName);

      const billingCustomer = await getFieldValuesByTableAndId({
        tableName: "masterdata.tms_m_stakeholder",
        primaryKey: "stk_id",
        fieldNames: "stk_name",
        ids: eihBillingCustomer ? eihBillingCustomer.toString() : "",
      });

      const billingCustomerName =
        billingCustomer && billingCustomer.length > 0
          ? billingCustomer[0].field1
          : "";
      setBillingCustomerName(billingCustomerName);

      //for summary dropdown
      let jobSet = [];
      for (let jobCharge of chargeList) {
        jobSet.push({
          key: jobCharge.jobId,
          text: `${jobCharge.jobCode} (${jobCharge.chhCode})`,
          value: jobCharge.jobId,
        });
      }
      setJobOptions(jobSet);
      setJobId(jobSet[0].value);
      function mapJobChargesWithJobId(data) {
        return data.reduce((acc, job) => {
          job.jobCharges.forEach((charge) => {
            if (!acc[charge.eidJob]) {
              acc[charge.eidJob] = [];
            }
            acc[charge.eidJob].push({
              jobCode: charge.eidJobCode,
              chargeCode: charge.chhCode,
              chargeType: charge.eidChargeValue,
              currency: charge.eidCurrencyName,
              total: formatValuesWithCurrency(
                formatToTwoDecimalPlaces(charge.eidTotal)
              ),
              status: charge.eidStatusValue,
              active: charge.eidActiveValue,
            });
          });
          return acc;
        }, {});
      }
      const jobChargesSet = mapJobChargesWithJobId(chargeList);
      console.log("jobCharges", jobChargesSet);
      setJobObject(jobChargesSet);
    })();
  }, [
    data,
    eihActive,
    eihStatus,
    eihCurrency,
    eihInvCustomer,
    eihBillingCustomer,
  ]);

  // const formatToTwoDecimalPlaces = (value) => {
  //   if (value == null || value === "N/A") {
  //     return "N/A";
  //   }

  //   const num = Number(value);
  //   if (isNaN(num)) return "N/A";

  //   const parts = num.toFixed(2).split(".");
  //   const whole = Number(parts[0]).toLocaleString("en-US");
  //   const decimal = parts[1] ? "." + parts[1] : "";

  //   return `${userCurrencyCode} ${whole}${decimal}`;
  // };

  return (
    <div>
      <h2
        style={{
          paddingTop: "1rem",
        }}
      >
        {eihType === 2 ? "EV" : "INV"} Summary
      </h2>
      <hr />
      <div
        style={{
          marginBottom: "1rem",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div>
          <p>
            <strong>Invoice Code : </strong> {eihCode ? eihCode : "N/A"}
          </p>
          <p>
            <strong>Type : </strong> {eihType === 2 ? "EV" : "INV"}
          </p>
          <p>
            <strong>Invoice Customer : </strong>{" "}
            {eihInvCustomerName ? eihInvCustomerName : "N/A"}
          </p>
          <p>
            <strong>Transporter : </strong>{" "}
            {eihTransporterName ? eihTransporterName : "N/A"}
          </p>

          <p>
            <strong>Billing Customer : </strong>{" "}
            {billingCustomerName ? billingCustomerName : "N/A"}
          </p>
          <p>
            <strong>Created On : </strong> {createdDate.split("T")[0]}
          </p>
        </div>
        <div>
          <p>
            <strong>Currency : </strong> {currencyName ? currencyName : "N/A"}
          </p>
          <p>
            <strong>Amount (Without Tax) : </strong>{" "}
            {formatValuesWithCurrency(formatToTwoDecimalPlaces(eihWithoutTax))}
          </p>
          <p>
            <strong>Amount (With Tax) : </strong>{" "}
            {formatValuesWithCurrency(formatToTwoDecimalPlaces(eihWithTax))}
          </p>
          <p>
            <strong>Status : </strong> {statusName ? statusName : "N/A"}
          </p>
          <p>
            <strong>Active : </strong> {activeName ? activeName : "N/A"}
          </p>
        </div>
      </div>
      <div className="ui form segment">
        <div className="two fields">
          <Dropdown
            label="Select job to see Charges"
            clearable={false}
            options={jobOptions}
            value={jobId}
            onChange={(e, { value }) => {
              setJobId(value);
            }}
            placeholder="Select Job"
          />
        </div>
        {chargeList && chargeList.length > 0 ? (
          jobObject[jobId]?.map((job, index) => (
            <div
              key={index}
              style={{
                marginBottom: "0.5rem",
                marginTop: "0.5rem",
              }}
            >
              <ul
                style={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <li style={{ marginBottom: "0.5rem" }}>
                  <span>
                    <strong>Charge Type:</strong> {job.chargeType} |{" "}
                  </span>
                  <span>
                    <strong>Total:</strong> {job.total}
                  </span>
                </li>
              </ul>
            </div>
          ))
        ) : (
          <p>No charge details available.</p>
        )}
      </div>
    </div>
  );
};

const CreateCreditDebitDraft = ({
  invoiceDetails,
  editMode,
  noteType,
  creditDebitDetails,
  handleDraftDrawerClose,
  draftApprovalMode,
  remark,
  setRemark,
  dhdId,
  setUpdateNeeded,
}) => {
  const user = useSelector((state) => state.user);
  const [jobCodesOptions, setJobCodesOptions] = useState([]);
  const [selectRendererJobOptions, setSelectRendererJobOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [tab, setTab] = useState(1);
  const [existingCharges, setExistingCharges] = useState({});
  const [chargeTypeOptions, setChargeTypeOptions] = useState([]);
  const [activeAccordion, setActiveAccordion] = useState(false);
  const [chargeList, setChargeList] = useState([]);
  const [rowCount, setRowCount] = useState(1);
  const [currentErrors, setCurrentErrors] = useState({});
  const userCompany = useUserCompany();
  const userCurrencyCode = userCompany ? userCompany.comCurrencyCode : null;
  const [existingTotalSet, setExistingTotalSet] = useState([]);
  const [statusOpt, setStatusOpt] = useState([]);
  const [activeOpt, setActiveOpt] = useState([]);
  const [chargeOpt, setChargeOpt] = useState([]);
  const [uomOpt, setUomOpt] = useState([]);
  const [detailListAgainstChhId, setDetailListAgainstChhId] = useState({});
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isDraftSubmitLoading, setIsDraftSubmitLoading] = useState(false);
  const [isDataAdding, setIsDataAdding] = useState(false);
  const [selectedCharge, setSelectedCharge] = useState({
    jobEvInvoicesTaxDetailList: {},
  });

  console.log("invoiceDetails", invoiceDetails);

  const formatToTwoDecimalPlaces = (value) => {
    if (value == null || value === "N/A") {
      return "N/A";
    }

    const num = parseFloat(value);
    if (isNaN(num)) return "N/A";

    return Number(num.toFixed(2)); // convert back to number!
  };

  const formatValuesWithCurrency = (value) => {
    if (value == null || value === "N/A") {
      return "N/A";
    }
    const formatter = Intl.NumberFormat("en-US", {
      style: "currency",
      currency: userCurrencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return formatter.format(value);
  };

  const [total, setTotal] = useState(formatValuesWithCurrency(0));
  const [entireAmount, setEntireAmount] = useState(formatValuesWithCurrency(0));
  const [entireTax, setEntireTax] = useState(formatValuesWithCurrency(0));

  const auth = useSelector((state) => state.user.permissions[MODULES.Finance]);
  const cdDraftPermission = auth[FORMS?.CREDIT_DEBIT.CD_DRAFT?.form];
  // const deletePermission = cdDraftPermission?.FORM?.DELETE;
  // const approvePermission = cdDraftPermission?.FORM?.CREATE;
  // const updatePermission = cdDraftPermission?.FORM?.UPDATE;

  const [company, setCompany] = useState(
    userCompany ? userCompany.comId : null
  );

  useEffect(() => {
    setCompany(userCompany ? userCompany.comId : null);
  }, [userCompany]);

  const [gridRow, setGridRow] = useState({
    chargeId: null,
    chargeName: "",
    chargeType: null,
    chargeTypeName: "",
    chargeValue: "",
    existingTotalValue: "",
    taxAmount: "",
    totalValue: "",
    noteType: noteType,
    noteTypeName: noteType === noteCategory.DEBIT ? "Debit" : "Credit",
    status: 2,
    statusName: "",
    active: 1,
    activeName: "",
    qty: "",
    uom: 1,
    uomName: "",
    taxList: [],
    newLine: false,
    existingRecord: false,
  });

  console.log("jobCodeOptions", jobCodesOptions);
  const formatter = useMemo(
    () =>
      userCurrencyCode
        ? Intl.NumberFormat("en-US", {
            style: "currency",
            currency: userCurrencyCode,
          })
        : null,
    [userCurrencyCode]
  );

  console.log("jobCodesOpt", jobCodesOptions);
  console.log("chargeOpt", chargeOpt);

  useEffect(() => {
    (async () => {
      console.log("invoiceDetails", invoiceDetails);
      setIsLoading(true);

      let jobSet = [];
      for (let jobCharge of invoiceDetails?.chargeList) {
        jobSet.push({
          key: jobCharge.chhId,
          text: `${jobCharge.jobCode} (${jobCharge.chhCode})`,
          value: jobCharge.chhId,
        });
      }
      setJobCodesOptions(jobSet);

      let selectRendererJobSet = [];
      for (let jobCharge of invoiceDetails?.chargeList) {
        selectRendererJobSet.push({
          id: jobCharge.chhId,
          text: `${jobCharge.jobCode} (${jobCharge.chhCode})`,
          value: jobCharge.chhId,
        });
      }
      setSelectRendererJobOptions(selectRendererJobSet);

      function mapJobCharges(data) {
        return data.reduce((acc, job) => {
          job.jobCharges.forEach((charge) => {
            if (!acc[charge.chhId]) {
              acc[charge.chhId] = [];
            }
            acc[charge.chhId].push({
              key: charge.eidId,
              text: `${charge.eidChargeValue} (${formatToTwoDecimalPlaces(charge.eidTotal)})`,
              value: charge.eidId,
            });
          });
          return acc;
        }, {});
      }

      const chargeTypes = mapJobCharges(invoiceDetails?.chargeList);
      setExistingCharges(chargeTypes);

      function mapExistingTotal(data) {
        return data.reduce((acc, job) => {
          job.jobCharges.forEach((charge) => {
            if (!acc[charge.eidId]) {
              acc[charge.eidId] = [];
            }
            acc[charge.eidId].push({
              existingTotal: charge.eidTotal,
              existingUom: charge.eidUom,
              existingUomName: charge.eidUomValue,
              existingQty: charge.eidQty,
              existingStatus: charge.eidStatus,
              existingStatusName: charge.eidStatusValue,
              existingActive: charge.eidActive,
              existingActiveName: charge.eidActiveValue,
            });
          });
          return acc;
        }, {});
      }

      const existingCharges = mapExistingTotal(invoiceDetails?.chargeList);
      setExistingTotalSet(existingCharges);

      function getDetailsUsingChhId(data) {
        let chhWiseCharges = {};
        for (let charge of data) {
          chhWiseCharges = {
            ...chhWiseCharges,
            [charge.chhId]: chhWiseCharges[charge.chhId]
              ? {
                  ...chhWiseCharges[charge.chhId],
                  ...charge,
                  jobCharges: charge.jobCharges.reduce((acc, jc) => {
                    acc[jc.eidId] = jc;
                    return acc;
                  }, {}),
                }
              : {
                  ...charge,
                  jobCharges: charge.jobCharges.reduce((acc, jc) => {
                    acc[jc.eidId] = jc;
                    return acc;
                  }, {}),
                },
          };
        }
        return chhWiseCharges;
      }

      const detailsAgainstChhId = getDetailsUsingChhId(
        invoiceDetails?.chargeList
      );
      setDetailListAgainstChhId(detailsAgainstChhId);

      if (draftApprovalMode) {
        let existingList = [];
        for (let item of creditDebitDetails?.dddList) {
          const taxes = await calculateCreditDebitTax({
            chId: item.dddCharge,
            chAmount: item.dddValue ? item.dddValue : 0,
            stakeholderId:
              invoiceDetails?.eihType === 2
                ? invoiceDetails?.eihTransporter
                : invoiceDetails?.eihInvCustomer,
          });

          if (item.dddNewLine) {
            existingList.push({
              dcdId: item.dddId,
              dcdCode: item.dddCode,
              dcdJob: item.dddJob,
              dcdDescription: item.dddDescription,
              dcdCurrency: item.dddCurrency,
              totalValue: item.dddTotal, //
              dcdTax: item.dddTax, //
              taxAmount: item.dddTaxAmount, //
              dcdExcId: item.dddExcId,
              dcdExcRate: item.dddExcRate,
              qty: item.dddQty,
              uom: item.dddUom,
              uomName: item.dddUomName,
              chargeId: item.dddChhId,
              chargeName: jobSet?.find((j) => j.value === item.dddChhId).text,
              chargeType: item.dddCharge,
              chargeTypeId: item.dddCharge,
              chargeTypeName: item.dddChargeName,
              chargeValue: item.dddValue ? parseFloat(item.dddValue) : 0,
              existingTotalValue: 0,
              noteType: noteType,
              noteTypeName:
                noteType === noteCategory.DEBIT ? "Debit" : "Credit",
              status: item.dddStatus,
              statusName: item.dddStatusName,
              active: item.dddActive,
              activeName: item.dddActiveName,
              newLine: true,
              existingRecord: true,
              taxList: taxes?.map((ti) => ({
                eitReference: null,
                eitCompany: creditDebitDetails.dhdCompany,
                eitTad: ti.tadId,
                eitTax: ti.taxId,
                taxName: ti.taxTax,
                eitTadValue: ti.taxPercentage,
                eitValue: ti.taxAmount,
                eitStatus: 2,
                eitActive: 1,
                beforeTax: ti.beforeTaxAmount,
                afterTax: ti.afterTaxAmount,
                author: user.username,
              })),
            });
          } else {
            existingList.push({
              dcdId: item.dddId,
              dcdCode: item.dddCode,
              dcdJob: item.dddJob,
              chargeId: item.dddChhId,
              chargeName: jobSet?.find((j) => j.value === item.dddChhId).text, //item.dcdChhId,
              chargeType: item.dddEidId,
              chargeTypeId: item.dddCharge,
              chargeTypeName: item.dddChargeName,
              chargeValue: item.dddValue ? parseFloat(item.dddValue) : 0,
              dcdDescription: item.dddDescription,
              dcdCurrency: item.dddCurrency,
              totalValue: item.dddTotal, //
              dcdTax: item.dddTax, //
              taxAmount: item.dddTaxAmount, //
              dcdExcId: item.dddExcId,
              dcdExcRate: item.dddExcRate,
              qty: item.dddQty,
              uom: item.dddUom,
              uomName: item.dddUomName,
              existingTotalValue: item.dddExistingValue
                ? parseFloat(item.dddExistingValue)
                : 0,
              noteType: noteType,
              noteTypeName:
                noteType === noteCategory.DEBIT ? "Debit" : "Credit",
              status: item.dddStatus,
              statusName: item.dddStatusName,
              active: item.dddActive,
              activeName: item.dddActiveName,
              newLine: false,
              existingRecord: true,
              taxList: taxes?.map((ti) => ({
                eitReference: null,
                eitCompany: creditDebitDetails.dhdCompany,
                eitTad: ti.tadId,
                eitTax: ti.taxId,
                taxName: ti.taxTax,
                eitTadValue: ti.taxPercentage,
                eitValue: ti.taxAmount,
                eitStatus: 2,
                eitActive: 1,
                beforeTax: ti.beforeTaxAmount,
                afterTax: ti.afterTaxAmount,
                author: user.username,
              })),
            });
          }
        }
        let rows = existingList?.length + 1;
        setRowCount(rows);
        setChargeList(existingList);

        const total = existingList.reduce(
          (acc, curr) => formatToTwoDecimalPlaces(acc) + formatToTwoDecimalPlaces(curr.totalValue),
          0
        );
        setTotal(formatValuesWithCurrency(total));

        const taxTotal = existingList.reduce(
          (acc, curr) => formatToTwoDecimalPlaces(acc) + formatToTwoDecimalPlaces(curr.taxAmount),
          0
        );
        setEntireTax(formatValuesWithCurrency(taxTotal));

        const amount = existingList.reduce(
          (acc, curr) => formatToTwoDecimalPlaces(acc) + formatToTwoDecimalPlaces(curr.chargeValue),
          0
        );
        setEntireAmount(formatValuesWithCurrency(amount));
      }

      setRemark(creditDebitDetails?.dhdRemarks);
      setIsLoading(false);
    })();
  }, [invoiceDetails, draftApprovalMode, creditDebitDetails]);

  console.log("details", creditDebitDetails);
  console.log("Total", total);

  useEffect(() => {
    if (gridRow.chargeId) {
      const chOpt = (chhId) => existingCharges[chhId] || [];
      setChargeTypeOptions(chOpt(gridRow.chargeId));
    }
  }, [gridRow.chargeId, existingCharges]);

  const resetForm = () => {
    setGridRow({
      chargeId: null,
      chargeName: "",
      chargeType: null,
      chargeTypeName: "",
      chargeValue: "",
      taxAmount: "",
      totalValue: "",
      noteType: noteType,
      noteTypeName: noteType === noteCategory.DEBIT ? "Debit" : "Credit",
      status: 2,
      statusLabel: "",
      active: 1,
      activeLabel: "",
      qty: "",
      uom: 1,
      uomName: "",
      existingTotalValue: "",
      taxList: [],
      newLine: false,
      existingRecord: false,
    });

    setCurrentErrors({});
    setChargeTypeOptions([]);
  };

  const resetGrid = () => {
    resetForm();
    setChargeList([]);
    setRemark("");
  };

  const validateAddClick = () => {
    let errors = {};
    if (!gridRow.chargeId || gridRow.chargeId === null) {
      errors = {
        ...errors,
        chargeId: "Job is required",
      };
    }

    if (!gridRow.chargeType || gridRow.chargeType === null) {
      errors = {
        ...errors,
        chargeType: "Charge Type is required",
      };
    }

    if (gridRow.chargeValue === "") {
      errors = {
        ...errors,
        chargeValue: "Value is required",
      };
    }
    return errors;
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const {
        status,
        active,
        uom: uom,
      } = await getBulkDropdownData([
        { key: "active" },
        { key: "status" },
        { key: "uom" },
      ]);
      setStatusOpt(status);
      setActiveOpt(active);
      setUomOpt(uom);

      const charges = await getAllCharges();
      setChargeOpt(charges);

      setIsLoading(false);
    })();
  }, [draftApprovalMode]);

  useEffect(() => {
    if (chargeList?.length > 0) {
      const total = chargeList.reduce(
        (acc, curr) => formatToTwoDecimalPlaces(acc) + formatToTwoDecimalPlaces(curr.totalValue),
        0
      );
      setTotal(formatValuesWithCurrency(total));
      // setTotal(total);

      const taxTotal = chargeList.reduce(
        (acc, curr) => formatToTwoDecimalPlaces(acc) + formatToTwoDecimalPlaces(curr.taxAmount),
        0
      );
      setEntireTax(formatValuesWithCurrency(taxTotal));

      const amount = chargeList.reduce(
        (acc, curr) => formatToTwoDecimalPlaces(acc) + formatToTwoDecimalPlaces(curr.chargeValue),
        0
      );
      setEntireAmount(formatValuesWithCurrency(amount));
    }
  }, [chargeList]);

  const handleAdd = async () => {
    setIsDataAdding(true);

    // //check whether chargeType is duplicated
    // const existingChargeType = chargeList?.find(
    //   (item) => item.chargeType === gridRow.chargeType
    // );
    // if (existingChargeType) {
    //   toast.warn("Charge Type already exist", {
    //     autoClose: 5000,
    //     containerId: "Main",
    //   });
    //   setIsDataAdding(false);
    //   return;
    // }

    const errors = validateAddClick();
    if (Object.keys(errors).length > 0) {
      setCurrentErrors(errors);
      toast.error("Please Fill the required fields", {
        autoClose: 5000,
        containerId: "Main",
      });
      setIsDataAdding(false);
      return;
    }

    const activeValue = activeById[gridRow.active];
    const statusValue = statusById[gridRow.status];

    const charge = jobCodesOptions?.find(
      (item) => item.value === gridRow.chargeId
    );
    const chgName = charge ? charge.text : "";

    const chgType = chargeTypeOptions?.find(
      (item) => item.value === gridRow.chargeType
    );
    const chgTypeName = chgType ? chgType.text : "";

    const taxes = await calculateCreditDebitTax({
      chId: detailListAgainstChhId[gridRow.chargeId].jobCharges[
        gridRow.chargeType
      ].eidCharge,
      chAmount: gridRow.chargeValue ? parseFloat(gridRow.chargeValue) : 0,
      stakeholderId:
        invoiceDetails?.eihType === 2
          ? invoiceDetails?.eihTransporter
          : invoiceDetails?.eihInvCustomer,
    });

    let totTax = 0;
    if (taxes.length > 0) {
      totTax = taxes?.map((item) => formatToTwoDecimalPlaces(item.taxAmount)).reduce((a, b) => a + b, 0);
    }

    let total = 0;
    if (noteType === noteCategory.DEBIT) {
      total =
        formatToTwoDecimalPlaces(parseFloat(gridRow.existingTotalValue)) +
        formatToTwoDecimalPlaces(parseFloat(totTax)) +
        formatToTwoDecimalPlaces(parseFloat(gridRow.chargeValue));
    } else {
      total =
        formatToTwoDecimalPlaces(parseFloat(gridRow.existingTotalValue)) -
        formatToTwoDecimalPlaces(parseFloat(totTax)) -
        formatToTwoDecimalPlaces(parseFloat(gridRow.chargeValue));
    }

    if (!gridRow.dcdId || gridRow.dcdId === null) {
      if (!gridRow.id || gridRow.id === null) {
        setChargeList((prev) => [
          ...prev,
          {
            ...gridRow,
            id: rowCount,
            chargeName: chgName,
            chargeTypeName: chgTypeName,
            taxAmount: totTax,
            totalValue: total,
            activeName: activeValue,
            statusName: statusValue,
            taxList: taxes?.map((ti) => ({
              eitReference: null,
              eitCompany: company,
              eitTad: ti.tadId,
              eitTax: ti.taxId,
              taxName: ti.taxTax,
              eitTadValue: ti.taxPercentage,
              eitValue: ti.taxAmount,
              eitStatus: 2,
              eitActive: 1,
              beforeTax: ti.beforeTaxAmount,
              afterTax: ti.afterTaxAmount,
              author: user.username,
            })),
          },
        ]);

        setRowCount((prevState) => prevState + 1);
      } else {
        //update
        setChargeList((prevState) =>
          prevState.filter((item) => item.id !== gridRow.id)
        );
        setChargeList((prev) => [
          ...prev,
          {
            ...gridRow,
            chargeName: chgName,
            chargeTypeName: chgTypeName,
            taxAmount: totTax,
            totalValue: total,
            activeName: activeValue,
            statusName: statusValue,
            taxList: taxes?.map((ti) => ({
              eitReference: null,
              eitCompany: company,
              eitTad: ti.tadId,
              eitTax: ti.taxId,
              taxName: ti.taxTax,
              eitTadValue: ti.taxPercentage,
              eitValue: ti.taxAmount,
              eitStatus: 2,
              eitActive: 1,
              beforeTax: ti.beforeTaxAmount,
              afterTax: ti.afterTaxAmount,
              author: user.username,
            })),
          },
        ]);
      }
    } else {
      setChargeList((prevState) =>
        prevState.filter((item) => item.dcdId !== gridRow.dcdId)
      );

      setChargeList((prev) => [
        ...prev,
        {
          ...gridRow,
          chargeName: chgName,
          chargeTypeName: chgTypeName,
          taxAmount: totTax,
          totalValue: total,
          activeName: activeValue,
          statusName: statusValue,
          taxList: taxes?.map((ti) => ({
            eitReference: null,
            eitCompany: company,
            eitTad: ti.tadId,
            eitTax: ti.taxId,
            taxName: ti.taxTax,
            eitTadValue: ti.taxPercentage,
            eitValue: ti.taxAmount,
            eitStatus: 2,
            eitActive: 1,
            beforeTax: ti.beforeTaxAmount,
            afterTax: ti.afterTaxAmount,
            author: user.username,
          })),
        },
      ]);
    }

    resetForm();

    setIsDataAdding(false);
  };

  const addNewLine = () => {
    const newRow = {
      id: rowCount,
      chargeId: null,
      chargeName: "",
      chargeType: null,
      chargeTypeName: "",
      chargeValue: 0,
      taxAmount: "",
      totalValue: "",
      existingTotalValue: 0,
      noteType: noteType,
      noteTypeName: noteType === noteCategory.DEBIT ? "Debit" : "Credit",
      status: 2,
      statusName: "",
      active: 1,
      activeName: "",
      qty: 1,
      uom: 1,
      uomName: "",
      taxList: [],
      newLine: true,
      existingRecord: false,
    };

    setChargeList([...chargeList, newRow]);
    setRowCount((prevState) => prevState + 1);
  };

  console.log("Charge List", chargeList);

  const cellEditorParams = async (params) => {
    switch (params.colDef.field) {
      case "chargeValue": {
        setIsDataAdding(true);

        let value = Number(params.value);
        let amount = parseFloat(value) * parseFloat(params.data.qty);

        const taxes = await calculateCreditDebitTax({
          chId: params.data.chargeType,
          chAmount: amount ? amount : 0,
          stakeholderId:
            invoiceDetails?.eihType === 2
              ? invoiceDetails?.eihTransporter
              : invoiceDetails?.eihInvCustomer,
        });

        let totTax = 0;
        if (taxes.length > 0) {
          totTax = taxes
            .map((item) => formatToTwoDecimalPlaces(item.taxAmount))
            .reduce((a, b) => a + b, 0);
        }

        let total1 = 0;
        if (noteType === noteCategory.DEBIT) {
          total1 =
            formatToTwoDecimalPlaces(parseFloat(params.data.existingTotalValue)) +
            formatToTwoDecimalPlaces(parseFloat(totTax)) +
            formatToTwoDecimalPlaces(parseFloat(amount));
        } else {
          total1 =
            formatToTwoDecimalPlaces(parseFloat(params.data.existingTotalValue)) -
            formatToTwoDecimalPlaces(parseFloat(totTax)) -
            formatToTwoDecimalPlaces(parseFloat(amount));
        }

        setChargeList((prev) =>
          prev.map((row) => {
            if (!params.data.dcdId) {
              if (row.id === params.data.id) {
                return {
                  ...row,
                  chargeValue: parseFloat(value),
                  taxAmount: totTax,
                  totalValue: total1,
                  taxList: taxes?.map((ti) => ({
                    eitReference: null,
                    eitCompany: company,
                    eitTad: ti.tadId,
                    eitTax: ti.taxId,
                    taxName: ti.taxTax,
                    eitTadValue: ti.taxPercentage,
                    eitValue: ti.taxAmount,
                    eitStatus: 2,
                    eitActive: 1,
                    beforeTax: ti.beforeTaxAmount,
                    afterTax: ti.afterTaxAmount,
                    author: user.username,
                  })),
                };
              }
            } else {
              if (row.dcdId === params.data.dcdId) {
                return {
                  ...row,
                  chargeValue: parseFloat(value),
                  taxAmount: totTax,
                  totalValue: total1,
                  taxList: taxes?.map((ti) => ({
                    eitReference: null,
                    eitCompany: company,
                    eitTad: ti.tadId,
                    eitTax: ti.taxId,
                    taxName: ti.taxTax,
                    eitTadValue: ti.taxPercentage,
                    eitValue: ti.taxAmount,
                    eitStatus: 2,
                    eitActive: 1,
                    beforeTax: ti.beforeTaxAmount,
                    afterTax: ti.afterTaxAmount,
                    author: user.username,
                  })),
                };
              }
            }
            return row;
          })
        );
        setIsDataAdding(false);
        break;
      }
      case "qty": {
        setIsDataAdding(true);

        let value = Number(params.value);
        let amount = parseFloat(value) * parseFloat(params.data.chargeValue);

        const taxes = await calculateCreditDebitTax({
          chId: params.data.chargeType,
          chAmount: amount ? amount : 0,
          stakeholderId:
            invoiceDetails?.eihType === 2
              ? invoiceDetails?.eihTransporter
              : invoiceDetails?.eihInvCustomer,
        });

        let totTax = 0;
        if (taxes.length > 0) {
          totTax = taxes
            .map((item) => formatToTwoDecimalPlaces(item.taxAmount))
            .reduce((a, b) => a + b, 0);
        }

        let total = 0;
        if (noteType === noteCategory.DEBIT) {
          total =
            formatToTwoDecimalPlaces(parseFloat(params.data.existingTotalValue)) +
            formatToTwoDecimalPlaces(parseFloat(totTax)) +
            formatToTwoDecimalPlaces(parseFloat(amount));
        } else {
          total =
            formatToTwoDecimalPlaces(parseFloat(params.data.existingTotalValue)) -
            formatToTwoDecimalPlaces(parseFloat(totTax)) -
            formatToTwoDecimalPlaces(parseFloat(amount));
        }

        setChargeList((prev) =>
          prev.map((row) => {
            if (!params.data.dcdId) {
              if (row.id === params.data.id) {
                return {
                  ...row,
                  qty: value,
                  taxAmount: totTax,
                  totalValue: total,
                  taxList: taxes?.map((ti) => ({
                    eitReference: null,
                    eitCompany: company,
                    eitTad: ti.tadId,
                    eitTax: ti.taxId,
                    taxName: ti.taxTax,
                    eitTadValue: ti.taxPercentage,
                    eitValue: ti.taxAmount,
                    eitStatus: 2,
                    eitActive: 1,
                    beforeTax: ti.beforeTaxAmount,
                    afterTax: ti.afterTaxAmount,
                    author: user.username,
                  })),
                };
              }
            } else {
              if (row.dcdId === params.data.dcdId) {
                return {
                  ...row,
                  qty: value,
                  taxAmount: totTax,
                  totalValue: total,
                  taxList: taxes?.map((ti) => ({
                    eitReference: null,
                    eitCompany: company,
                    eitTad: ti.tadId,
                    eitTax: ti.taxId,
                    taxName: ti.taxTax,
                    eitTadValue: ti.taxPercentage,
                    eitValue: ti.taxAmount,
                    eitStatus: 2,
                    eitActive: 1,
                    beforeTax: ti.beforeTaxAmount,
                    afterTax: ti.afterTaxAmount,
                    author: user.username,
                  })),
                };
              }
            }
            return row;
          })
        );
        setIsDataAdding(false);
        break;
      }
      default:
        break;
    }
  };

  console.log("Draft Approval Mode: ", draftApprovalMode);

  const handleDraftSave = async () => {
    setIsDraftSubmitLoading(true);
    try {
      if (!draftApprovalMode) {
        const data = {
          dhdReferance: invoiceDetails.eihReferance,
          dhdCompany: company,
          dhdSingleBulk: invoiceDetails.eihSingleBulk,
          dhdType: invoiceDetails.eihType,
          dhdCategory: noteType,
          dhdEvinvId: invoiceDetails.eihId,
          dhdCustomer: invoiceDetails.eihCustomer,
          dhdTransporter: invoiceDetails.eihTransporter,
          dhdBillingCustomer: invoiceDetails.eihBillingCustomer,
          dhdInvCustomer: invoiceDetails.eihInvCustomer,
          dhdAddress: invoiceDetails.eihAddress,
          dhdBank: invoiceDetails.eihBank,
          dhdAcc: invoiceDetails.eihAcc,
          dhdExchange: invoiceDetails.eihExchange,
          dhdCurrency: invoiceDetails.eihCurrency,
          dhdRemarks: remark, //header remark
          dhdWithoutTax: invoiceDetails.eihWithoutTax,
          dhdWithTax: invoiceDetails.eihWithTax,
          dhdAdHoc: invoiceDetails.eihAdHoc,
          // dhdIntegrationStatus: invoiceDetails.eihIntegrationStatus,
          dhdStatus: 1,
          dhdActive: 1,
          creditDebitDetailDraftRequestList: chargeList?.map((item) => ({
            dddReferance: null,
            dddCompany: company,
            dddChhId: item.chargeId,
            dddEidId: item.newLine ? null : item.chargeType,
            dddPickChargeId: item.newLine
              ? invoiceDetails.eihId
              : invoiceDetails.eihId, //eihId
            dddJob: detailListAgainstChhId[item.chargeId].jobId,
            dddCharge: item.newLine
              ? item.chargeType
              : detailListAgainstChhId[item.chargeId].jobCharges[
                  item.chargeType
                ].eidCharge,
            dddDescription: item.newLine
              ? ""
              : detailListAgainstChhId[item.chargeId].jobCharges[
                  item.chargeType
                ].eidDescription,
            dddCurrency: item.newLine
              ? invoiceDetails.eihCurrency
              : detailListAgainstChhId[item.chargeId].jobCharges[
                  item.chargeType
                ].eidCurrency,
            dddTotal: item.totalValue ? parseFloat(item.totalValue) : 0,
            dddTax: true,
            dddExcId: item.newLine
              ? null
              : detailListAgainstChhId[item.chargeId].jobCharges[
                  item.chargeType
                ].eidExcId,
            dddExcRate: item.newLine
              ? null
              : detailListAgainstChhId[item.chargeId].jobCharges[
                  item.chargeType
                ].eidExcRate,
            dddTaxAmount: item.taxAmount ? parseFloat(item.taxAmount) : 0,
            dddValue: parseFloat(item.chargeValue),
            dddQty: item.qty,
            dddUom: item.uom,
            dddStatus: item.status,
            dddActive: item.active,
            dddExistingValue: item.newLine ? null : item.existingTotalValue,
            dddNewLine: item.newLine,
            // taxList: item.taxList?.map((item) => ({
            //   eitReference: item.eitReference,
            //   eitCompany: company,
            //   eitOpe: 22,
            //   eitTad: item.eitTad,
            //   eitTax: item.eitTax,
            //   eitTadValue: item.eitTadValue,
            //   eitValue: item.eitValue,
            //   eitStatus: item.eitStatus,
            //   eitActive: item.eitActive,
            //   author: user.username,
            // })),
          })),
          author: user.username,
        };

        const response = await createCreditDebitDraft(data);

        if (response.status === 200) {
          toast.success(`${response.data.dhdCode} created successfully`, {
            autoClose: 5000,
            containerId: "Main",
          });
          resetGrid();
          handleDraftDrawerClose();
          setUpdateNeeded(true);
        } else {
          toast.error(response.data.message, {
            autoClose: 5000,
            containerId: "Main",
          });
        }
      } else {
        const data = {
          dhdId: creditDebitDetails.dhdId,
          dhdCode: creditDebitDetails.dhdCode,
          dhdReferance: creditDebitDetails.dhdReferance,
          dhdCompany: creditDebitDetails.dhdCompany,
          dhdSingleBulk: creditDebitDetails.dhdSingleBulk,
          dhdType: creditDebitDetails.dhdType,
          dhdCategory: creditDebitDetails.dhdCategory,
          dhdEvinvId: creditDebitDetails.dhdEvinvId,
          dhdCustomer: creditDebitDetails.dhdCustomer,
          dhdTransporter: creditDebitDetails.dhdTransporter,
          dhdBillingCustomer: creditDebitDetails.dhdBillingCustomer,
          dhdInvCustomer: creditDebitDetails.dhdInvCustomer,
          dhdAddress: creditDebitDetails.dhdAddress,
          dhdBank: creditDebitDetails.dhdBank,
          dhdAcc: creditDebitDetails.dhdAcc,
          dhdExchange: creditDebitDetails.dhdExchange,
          dhdCurrency: creditDebitDetails.dhdCurrency,
          dhdRemarks: remark,
          dhdWithoutTax: creditDebitDetails.dhdWithoutTax,
          dhdWithTax: creditDebitDetails.dhdWithTax,
          dhdAdHoc: creditDebitDetails.dhdAdHoc,
          // dhdIntegrationStatus: creditDebitDetails.dcrIntegrationStatus,
          dhdStatus: creditDebitDetails.dhdStatus,
          dhdActive: creditDebitDetails.dhdActive,
          creditDebitDetailDraftRequestList: chargeList?.map((item) => ({
            dddId: draftApprovalMode ? item.dcdId : null,
            dddCode: draftApprovalMode ? item.dcdCode : null,
            dddReferance: null,
            dddCompany: creditDebitDetails.dhdCompany,
            dddChhId: item.chargeId,
            dddEidId: item.newLine ? null : item.chargeType,
            dddPickChargeId: creditDebitDetails.dhdEvinvId, //eihId
            // dddJob: item.existingRecord
            //   ? item.dcdJob
            //   : detailListAgainstChhId[item.chargeId].jobId,
            dddJob: detailListAgainstChhId[item.chargeId].jobId,
            // dddCharge: item.existingRecord
            //   ? item.newLine
            //     ? item.chargeType
            //     : item.chargeTypeId
            //   : item.newLine
            //     ? item.chargeType
            //     : detailListAgainstChhId[item.chargeId].jobCharges[
            //         item.chargeType
            //       ].eidCharge,
            dddCharge: item.newLine
              ? item.chargeType
              : detailListAgainstChhId[item.chargeId].jobCharges[
                  item.chargeType
                ].eidCharge,
            dddDescription: item.existingRecord
              ? item.dcdDescription
              : item.newLine
                ? ""
                : detailListAgainstChhId[item.chargeId].jobCharges[
                    item.chargeType
                  ].eidDescription,
            dddCurrency: item.existingRecord
              ? item.dcdCurrency
              : item.newLine
                ? creditDebitDetails.dhdCurrency
                : detailListAgainstChhId[item.chargeId].jobCharges[
                    item.chargeType
                  ].eidCurrency,
            dddTotal: item.totalValue ? parseFloat(item.totalValue) : 0,
            dddTax: item.existingRecord ? item.dcdTax : true,
            dddTaxAmount: item.taxAmount ? parseFloat(item.taxAmount) : 0,
            dddExcId: item.existingRecord
              ? item.dcdExcId
              : item.newLine
                ? null
                : detailListAgainstChhId[item.chargeId].jobCharges[
                    item.chargeType
                  ].eidExcId,
            dddExcRate: item.existingRecord
              ? item.dcdExcRate
              : item.newLine
                ? null
                : detailListAgainstChhId[item.chargeId].jobCharges[
                    item.chargeType
                  ].eidExcRate,
            dddValue: parseFloat(item.chargeValue),
            dddQty: item.qty,
            dddUom: item.uom,
            dddStatus: item.status,
            dddActive: item.active,
            dddExistingValue: item.newLine ? 0 : item.existingTotalValue,
            dddNewLine: item.newLine,
          })),
          author: user.username,
        };

        console.log("submit data", data);
        const response = await updateCreditDebitDraft(data);

        if (response.status === 200) {
          toast.success(`${response.data.dhdCode} Updated successfully`, {
            autoClose: 5000,
            containerId: "Main",
          });
          resetGrid();
          handleDraftDrawerClose();
          setUpdateNeeded(true);
        } else {
          toast.error(response.data.message, {
            autoClose: 5000,
            containerId: "Main",
          });
        }
      }
    } catch (e) {
      console.log(e);
      toast.error("Something went wrong, please try again.");
    } finally {
      // handleDraftDrawerClose();
      setIsDraftSubmitLoading(false);
    }
    handleDraftDrawerClose();
    setIsDraftSubmitLoading(false);
  };

  const handleApproveDraft = async () => {
    setIsSubmitLoading(true);
    try {
      const data = {
        dcrReferance: creditDebitDetails.dhdReferance,
        dcrCompany: creditDebitDetails.dhdCompany,
        dcrSingleBulk: creditDebitDetails.dhdSingleBulk,
        dcrType: creditDebitDetails.dhdType,
        dcrCategory: creditDebitDetails.dhdCategory,
        dcrEvinvId: creditDebitDetails.dhdEvinvId,
        dcrCustomer: creditDebitDetails.dhdCustomer,
        dcrTransporter: creditDebitDetails.dhdTransporter,
        dcrBillingCustomer: creditDebitDetails.dhdBillingCustomer,
        dcrInvCustomer: creditDebitDetails.dhdInvCustomer,
        dcrAddress: creditDebitDetails.dhdAddress,
        dcrBank: creditDebitDetails.dhdBank,
        dcrAcc: creditDebitDetails.dhdAcc,
        dcrExchange: creditDebitDetails.dhdExchange,
        dcrCurrency: creditDebitDetails.dhdCurrency,
        dcrRemarks: remark, //header remark
        dcrWithoutTax: creditDebitDetails.dhdWithoutTax,
        dcrWithTax: creditDebitDetails.dhdWithTax,
        dcrAdHoc: creditDebitDetails.dhdAdHoc,
        dcrIntegrationStatus: 2,
        dcrStatus: 2,
        dcrActive: creditDebitDetails.dhdActive,
        dcrPrinted: false,
        dcrPrePrinted : false,
        creditDebitDetailRequestList: chargeList?.map((item) => ({
          dcdReferance: null,
          dcdCompany: creditDebitDetails.dhdCompany,
          dcdChhId: item.chargeId,
          dcdEidId: item.newLine ? null : item.chargeType,
          dcdPickChargeId: creditDebitDetails.dhdEvinvId, //eihId
          dcdJob: detailListAgainstChhId[item.chargeId].jobId,
          dcdCharge: item.newLine
            ? item.chargeType
            : detailListAgainstChhId[item.chargeId].jobCharges[item.chargeType]
                .eidCharge,
          dcdDescription: item.existingRecord
            ? item.dcdDescription
            : item.newLine
              ? ""
              : detailListAgainstChhId[item.chargeId].jobCharges[
                  item.chargeType
                ].eidDescription,
          dcdCurrency: item.existingRecord
            ? item.dcdCurrency
            : item.newLine
              ? creditDebitDetails.dhdCurrency
              : detailListAgainstChhId[item.chargeId].jobCharges[
                  item.chargeType
                ].eidCurrency,
          dcdTotal: item.totalValue ? parseFloat(item.totalValue) : 0,
          dcdTax: item.existingRecord ? item.dcdTax : true,
          dcdExcId: item.existingRecord
            ? item.dcdExcId
            : item.newLine
              ? null
              : detailListAgainstChhId[item.chargeId].jobCharges[
                  item.chargeType
                ].eidExcId,
          dcdExcRate: item.existingRecord
            ? item.dcdExcRate
            : item.newLine
              ? null
              : detailListAgainstChhId[item.chargeId].jobCharges[
                  item.chargeType
                ].eidExcRate,
          dcdTaxAmount: item.taxAmount ? parseFloat(item.taxAmount) : 0,
          dcdValue: parseFloat(item.chargeValue),
          dcdQty: item.qty,
          dcdUom: item.uom,
          dcdStatus: item.status,
          dcdActive: item.active,
          dcdExistingValue: item.newLine ? null : item.existingTotalValue,
          dcdNewLine: item.newLine,
          taxList: item.taxList?.map((item) => ({
            eitReference: item.eitReference,
            eitCompany: creditDebitDetails.dhdCompany,
            eitOpe: 22,
            eitTad: item.eitTad,
            eitTax: item.eitTax,
            eitTadValue: item.eitTadValue,
            eitValue: item.eitValue,
            eitStatus: item.eitStatus,
            eitActive: item.eitActive,
            author: user.username,
          })),
        })),
        author: user.username,
      };

      const response = await createCreditDebitNote(data);

      if (response.status === 200) {
        await deleteCreditDebitDraft(dhdId);

        toast.success(`${response.data.dcrCode} created successfully`, {
          autoClose: 5000,
          containerId: "Main",
        });
        resetGrid();
        handleDraftDrawerClose();
      } else {
        toast.error(response.data.message, {
          autoClose: 5000,
          containerId: "Main",
        });
      }
    } catch (e) {
      console.log(e);
      toast.error("Something went wrong, please try again.");
    } finally {
      // handleDraftDrawerClose();
      setIsSubmitLoading(false);
    }
    setIsSubmitLoading(false);
  };

  console.log("charge type options", chargeTypeOptions);

  return (
    <>
      {isSubmitLoading && (
        <div className="blur-overlay">
          <LoadingSpinner message={`Creating Credit Debit Note...`} />
        </div>
      )}
      {isDraftSubmitLoading && (
        <div className="blur-overlay">
          <LoadingSpinner
            message={`${draftApprovalMode ? `Updating draft...` : `Saving draft...`}`}
          />
        </div>
      )}
      {isLoading ? (
        <LoadingSpinner message={`Fetching Details...`} />
      ) : (
        <div className="ui form segment">
          <div className="two fields">
            <Input
              type={"text"}
              label="Remark"
              value={remark}
              placeholder="Remark"
              onChange={(e) => setRemark(e.target.value)}
            />
          </div>
          <div className="ui styled accordion" style={{ width: "100%" }}>
            <div
              className={`title ${activeAccordion ? "active" : ""}`}
              onClick={() => setActiveAccordion(!activeAccordion)}
              style={{
                cursor: "pointer",
              }}
            >
              {activeAccordion ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    {" "}
                    Click here to hide {invoiceDetails?.eihCode} details
                  </div>
                  <Minimize2 size={14} />
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    Click here to view {invoiceDetails?.eihCode} details
                  </div>
                  <Maximize2 size={14} />
                </div>
              )}
            </div>
            <AccordionContent isOpen={activeAccordion}>
              <SummaryDetail data={invoiceDetails} />
            </AccordionContent>
          </div>

          {chargeList?.length > 0 && (
            <ChargeTaxBreakdown
              formatter={formatter}
              selectedCharge={selectedCharge ? selectedCharge : null}
            />
          )}

          <div className="four fields" style={{ marginTop: "10px" }}>
            <Dropdown
              required={true}
              label="Jobs"
              search={true}
              clearable={false}
              options={jobCodesOptions}
              value={gridRow.chargeId}
              onChange={(e, { value }) => {
                setGridRow({
                  ...gridRow,
                  chargeId: value,
                });
              }}
              placeholder="Select Job"
              error={currentErrors.chargeId}
              resetError={() =>
                setCurrentErrors((prevState) => {
                  const { chargeId: _, ...rest } = prevState;
                  return rest;
                })
              }
              //   readOnly={draftApprovalMode ? true : false}
            />
            <Dropdown
              required={true}
              label="Charge Type"
              clearable={false}
              options={chargeTypeOptions}
              value={gridRow.chargeType}
              onChange={(e, { value }) => {
                setGridRow({
                  ...gridRow,
                  chargeType: value,
                  existingTotalValue: existingTotalSet[value][0]?.existingTotal,
                  qty: existingTotalSet[value][0]?.existingQty,
                  uom: existingTotalSet[value][0]?.existingUom,
                  uomName: existingTotalSet[value][0]?.existingUomName,
                  status: existingTotalSet[value][0]?.existingStatus,
                  statusName: existingTotalSet[value][0]?.existingStatusName,
                  active: existingTotalSet[value][0]?.existingActive,
                  activeName: existingTotalSet[value][0]?.existingActiveName,
                });
              }}
              placeholder={
                !gridRow.chargeId ? "Select Job" : "Select Charge Type"
              }
              error={currentErrors.chargeType}
              resetError={() =>
                setCurrentErrors((prevState) => {
                  const { chargeType: _, ...rest } = prevState;
                  return rest;
                })
              }
              //   readOnly={editMode ? true : false}
            />
            <Input
              type={"text"}
              label="Existing Value"
              value={
                gridRow.existingTotalValue
                  ? formatValuesWithCurrency(
                      formatToTwoDecimalPlaces(gridRow.existingTotalValue)
                    )
                  : ""
              }
              readOnly={true}
            />
            <Input
              required={true}
              type={"number"}
              label={"Value"}
              step={"0.0001"}
              value={gridRow.chargeValue}
              onChange={(e) =>
                setGridRow({
                  ...gridRow,
                  chargeValue: parseFloat(e.target.value),
                })
              }
              error={currentErrors.chargeValue}
              resetError={() =>
                setCurrentErrors((prevState) => {
                  const { chargeValue: _, ...rest } = prevState;
                  return rest;
                })
              }
              //   readOnly={editMode ? true : false}
            />
          </div>

          <div className="four fields">
            <Input
              type={"number"}
              label={"Existing Qty"}
              step={"0.0001"}
              value={gridRow.qty}
              readOnly={true}
            />
            <Input
              type={"text"}
              placeholder="Existing Uom"
              label="Existing Uom"
              value={gridRow.uomName}
              readOnly={true}
            />
            <Input
              type={"text"}
              placeholder="Existing Status"
              label="Existing Status"
              value={gridRow.statusName}
              readOnly={true}
            />
            <Input
              type={"text"}
              placeholder="Existing Active"
              label="Existing Active"
              value={gridRow.activeName}
              readOnly={true}
            />
          </div>

          {cdDraftPermission ? (
            <Button
              label={"New Line"}
              Icon={Plus}
              disabled={
                gridRow.chargeId || gridRow.chargeType || gridRow.chargeValue
              }
              onClick={addNewLine}
            />
          ) : (
            ""
          )}
          {cdDraftPermission ? (
            <Button
              label="Add"
              Icon={Plus}
              editMode={draftApprovalMode}
              onClick={handleAdd}
              // disabled={editMode ? true : false}
            />
          ) : (
            ""
          )}
          {cdDraftPermission ? (
            <Button
              label={"Reset"}
              Icon={RefreshCcw}
              onClick={resetForm}
              // disabled={editMode ? true : false}
            />
          ) : (
            ""
          )}

          <div className="four fields">
            <Input
              type={"text"}
              placeholder="Tot Amount"
              label="Tot Amount"
              value={entireAmount}
              readOnly={true}
            />
            <Input
              type={"text"}
              placeholder="Tot Tax"
              label="Tot Tax"
              value={entireTax}
              readOnly={true}
            />
            <Input
              type={"text"}
              placeholder="Total"
              label="Total"
              value={total}
              readOnly={true}
            />
          </div>
          <Grid
            cellClickHandler={(e) => {
              console.log("cellClickHandler", e.data.taxList);
              setSelectedCharge({
                jobEvInvoicesTaxDetailList: e.data.taxList,
              });
            }}
            content={chargeList}
            // heading={`Total: ${total}`}
            isLoading={isDataAdding}
            searchable={false}
            stopEditingWhenCellsLoseFocus={true}
            onCellEditingStopped={cellEditorParams}
            config={[
              {
                headerName: "Job/Charge code",
                field: "chargeId",
                cellRendererSelector: (param) => {
                  console.log("select param", param);
                  if (!param.data.newLine) {
                    return {
                      component: ResizableScrollingTextRenderer,
                      params: {
                        isCustomValue: true,
                        customValue: param.data.chargeName,
                      },
                    };
                  } else {
                    return {
                      component: SelectRenderer,
                      params: {
                        onChangeHandler: (data) => {
                          setChargeList((prev) =>
                            prev.map((item) => {
                              if (!param.data.dcdId) {
                                if (item.id === param.data.id) {
                                  return {
                                    ...item,
                                    chargeId: parseInt(data),
                                  };
                                }
                              } else {
                                if (item.dcdId === param.data.dcdId) {
                                  return {
                                    ...item,
                                    chargeId: parseInt(data),
                                  };
                                }
                              }
                              return item;
                            })
                          );
                        },
                        options: jobCodesOptions,
                        module: MODULES.Finance,
                        field: "chargeId",
                        placeholder: "Select Job",
                        value: param.data.chargeId,
                        // disabled: editMode ? true : false,
                        searchable: true,
                      },
                    };
                  }
                },
              },
              {
                headerName: "Charge Type",
                field: "chargeType",
                cellRendererSelector: (param) => {
                  if (!param.data.newLine) {
                    return {
                      component: ResizableScrollingTextRenderer,
                      params: {
                        isCustomValue: true,
                        customValue: param.data.chargeTypeName,
                      },
                    };
                  } else {
                    return {
                      component: SelectRenderer,
                      params: {
                        onChangeHandler: (data) => {
                          setChargeList((prev) =>
                            prev.map((item) => {
                              if (!param.data.dcdId) {
                                if (item.id === param.data.id) {
                                  return {
                                    ...item,
                                    chargeType: parseInt(data),
                                    chargeValue: 0,
                                    taxAmount: "",
                                    totalValue: "",
                                    existingTotalValue: 0,
                                  };
                                }
                              } else {
                                if (item.dcdId === param.data.dcdId) {
                                  return {
                                    ...item,
                                    chargeType: parseInt(data),
                                    chargeValue: 0,
                                    taxAmount: "",
                                    totalValue: "",
                                    existingTotalValue: 0,
                                  };
                                }
                              }
                              return item;
                            })
                          );
                        },
                        options: chargeOpt,
                        module: MODULES.Finance,
                        field: "chargeType",
                        placeholder: "Select Charge Type",
                        value: param.data.chargeType,
                        // disabled: editMode ? true : false,
                        searchable: true,
                      },
                    };
                  }
                },
              },
              {
                headerName: "Existing Value",
                field: "existingTotalValue",
                cellRenderer: DynamicCurrencyRenderer,
                cellRendererParams: (params) => ({
                  currencyCode: userCurrencyCode,
                }),
              },
              {
                headerName: "Amount",
                field: "chargeValue",
                cellRendererSelector: (param) => {
                  if (!param.data.newLine) {
                    return {
                      component: CurrencyRenderer,
                      params: {
                        formatter: formatter,
                        value: parseFloat(param.data.chargeValue),
                      },
                    };
                  } else {
                    return {
                      component: CurrencyRenderer,
                      params: {
                        formatter: formatter,
                        cellEditorFramework: "agNumberCellEditor",
                        value: parseFloat(param.data.chargeValue),
                        tooltipValueGetter: (params) => {
                          return (
                            params.data.newLine &&
                            "press Enter to set the value"
                          );
                        },
                      },
                    };
                  }
                },
                editable: (params) => params.data.newLine,
              },
              {
                headerName: "Qty",
                field: "qty",
                cellRendererSelector: (param) => {
                  if (!param.data.newLine) {
                    return {
                      component: () => param.data.qty,
                    };
                  } else {
                    return {
                      cellEditorFramework: "agNumberCellEditor",
                      value: parseFloat(param.data.qty),
                      tooltipValueGetter: (params) => {
                        return (
                          params.data.newLine && "press Enter to set the value"
                        );
                      },
                      //   editable: () => {
                      //     return editMode ? false : true;
                      //   },
                    };
                  }
                },
                editable: (params) => params.data.newLine,
              },
              {
                headerName: "Tax Amount",
                field: "taxAmount",
                cellRenderer: DynamicCurrencyRenderer,
                cellRendererParams: (params) => ({
                  currencyCode: userCurrencyCode,
                }),
              },
              {
                headerName: "Total",
                field: "totalValue",
                cellRenderer: DynamicCurrencyRenderer,
                cellRendererParams: (params) => ({
                  currencyCode: userCurrencyCode,
                }),
              },
              {
                headerName: "UOM",
                field: "uom",
                cellRendererSelector: (param) => {
                  if (!param.data.newLine) {
                    return {
                      component: () => param.data.uomName,
                    };
                  } else {
                    return {
                      component: SelectRenderer,
                      params: {
                        onChangeHandler: (data) => {
                          setChargeList((prev) =>
                            prev.map((item) => {
                              if (!param.data.dcdId) {
                                if (item.id === param.data.id) {
                                  return {
                                    ...item,
                                    uom: parseInt(data),
                                  };
                                }
                              } else {
                                if (item.dcdId === param.data.dcdId) {
                                  return {
                                    ...item,
                                    uom: parseInt(data),
                                  };
                                }
                              }
                              return item;
                            })
                          );
                        },
                        options: uomOpt,
                        placeholder: "Select UOM",
                        value: param.data.uom,
                        // disabled: editMode ? true : false,
                      },
                    };
                  }
                },
              },
              {
                headerName: "Credit/Debit",
                field: "noteTypeName",
              },
              {
                headerName: "Status",
                field: "status",
                cellRendererSelector: (param) => {
                  if (!param.data.newLine) {
                    return {
                      component: () => param.data.statusName,
                    };
                  } else {
                    return {
                      component: SelectRenderer,
                      params: {
                        onChangeHandler: (data) => {
                          setChargeList((prev) =>
                            prev.map((item) => {
                              if (!param.data.dcdId) {
                                if (item.id === param.data.id) {
                                  return {
                                    ...item,
                                    status: parseInt(data),
                                  };
                                }
                              } else {
                                if (item.dcdId === param.data.dcdId) {
                                  return {
                                    ...item,
                                    status: parseInt(data),
                                  };
                                }
                              }
                              return item;
                            })
                          );
                        },
                        options: statusOpt,
                        placeholder: "Select Status",
                        value: param.data.status,
                        // disabled: editMode ? true : false,
                      },
                    };
                  }
                },
              },
              //   {
              //     headerName: "Active",
              //     field: "active",
              //     cellRendererSelector: (param) => {
              //       if (!param.data.newLine) {
              //         return {
              //           component: () => param.data.activeName,
              //         };
              //       } else {
              //         return {
              //           component: SelectRenderer,
              //           params: {
              //             onChangeHandler: (data) => {
              //               setChargeList((prev) =>
              //                 prev.map((item) => {
              //                   if (!param.data.dcdId) {
              //                     if (item.id === param.data.id) {
              //                       return {
              //                         ...item,
              //                         active: parseInt(data),
              //                       };
              //                     }
              //                   } else {
              //                     if (item.dcdId === param.data.dcdId) {
              //                       return {
              //                         ...item,
              //                         active: parseInt(data),
              //                       };
              //                     }
              //                   }
              //                   return item;
              //                 })
              //               );
              //             },
              //             options: activeOpt,
              //             placeholder: "Select Active",
              //             value: param.data.active,
              //             disabled: editMode ? true : false,
              //           },
              //         };
              //       }
              //     },
              //   },
              {
                headerName: "Edit",
                cellRenderer: ButtonRenderer,
                cellRendererParams: (params) => ({
                  clickHandler: () => {
                    if (params.data.newLine) return;
                    setGridRow(params.data);
                  },
                  label: "Edit",
                  disabled: params.data.newLine || !cdDraftPermission,
                }),
              },
              {
                headerName: "Delete",
                cellRenderer: ButtonRenderer,
                cellRendererParams: (params) => ({
                  clickHandler: () => {
                    console.log("Deleting Row:", params.data);
                    if (params.data.dcdId) {
                      setChargeList((prevTaxes) =>
                        prevTaxes.filter(
                          (charge) => charge.dcdId !== params.data.dcdId
                        )
                      );
                    } else {
                      setChargeList((prevTaxes) =>
                        prevTaxes.filter(
                          (charge) => charge.id !== params.data.id
                        )
                      );
                    }

                    setRowCount((prevCount) => prevCount - 1);
                  },
                  label: "Delete",
                  disabled: !cdDraftPermission,
                  className: "ui red button",
                }),
              },
            ]}
          />
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              marginTop: "5px",
            }}
          >
            {cdDraftPermission ? (
              <div className="field" style={{ width: "100%" }}>
                {!draftApprovalMode ? (
                  <Button
                    label={"save as draft"}
                    isLoading={isDraftSubmitLoading}
                    onClick={handleDraftSave}
                    Icon={FaRegBookmark}
                  />
                ) : (
                  <Button
                    label={"update"}
                    isLoading={isDraftSubmitLoading}
                    onClick={handleDraftSave}
                    Icon={FaRegBookmark}
                  />
                )}
                {draftApprovalMode && cdDraftPermission ? (
                  <Button
                    label={"Approve Draft"}
                    isLoading={isSubmitLoading}
                    onClick={handleApproveDraft}
                    Icon={Check}
                  />
                ) : (
                  ""
                )}
              </div>
            ) : (
              ""
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CreateCreditDebitDraft;
